import React from "react";
import styled, { css, DefaultTheme } from "styled-components";
import {
  getNewLookTokens,
  newLookPhoneType,
  newLookType,
} from "common/NewLook/tokens.ts";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

export type RailStyle = "solid" | "dashed" | "none";

export interface TimelineRowData {
  key: string;
  /** "Entry 3", without the "/5" suffix. */
  title: string;
  /** The muted "/5" suffix. */
  total?: string;
  meta: string;
  /** Pre-formatted price, with its "$" when the quote is a dollar coin. */
  price?: React.ReactNode;
  color: string;
  pending: boolean;
}

interface NewLookTimelineRowProps {
  row: TimelineRowData;
  /** Rail half above and below the dot. */
  top: RailStyle;
  bottom: RailStyle;
}

/**
 * New-look timeline row: a solid dot in the event's colour (hollow and dashed
 * while the order is still ahead), the title with a muted meta line, and the
 * price in ink on the right.
 */
const NewLookTimelineRow = ({ row, top, bottom }: NewLookTimelineRowProps) => (
  <Item $pending={row.pending}>
    <Rail $top={top} $bottom={bottom}>
      <Dot $color={row.color} $pending={row.pending} />
    </Rail>
    <Body>
      <Main>
        <Title>
          {row.title}
          {row.total && <Total>{row.total}</Total>}
        </Title>
        <Meta>{row.meta}</Meta>
      </Main>
      {row.price && <Price>{row.price}</Price>}
    </Body>
  </Item>
);

export default React.memo(NewLookTimelineRow);

const DOT = 12;
const RAIL_WIDTH = 12;
export const DOT_TOP = 18;
/** Phone: one-line rows; the dot centres on the 34px row. */
const PHONE_ROW = 34;
const PHONE_DOT_TOP = (PHONE_ROW - DOT) / 2;
const phone = (props: { theme: DefaultTheme }) =>
  `@media (max-width: ${phoneMaxWidth(props)})`;

export const railSegment = (style: RailStyle, color?: string) =>
  style === "dashed"
    ? css`
        background: repeating-linear-gradient(
          to bottom,
          ${color} 0 4px,
          transparent 4px 8px
        );
      `
    : style === "solid"
    ? css`
        background: ${color};
      `
    : css`
        background: transparent;
      `;

export const Item = styled.div<{ $pending?: boolean }>`
  display: flex;
  gap: 14px;
  width: 100%;
`;
export const Rail = styled.div<{ $top: RailStyle; $bottom: RailStyle }>`
  position: relative;
  flex: none;
  width: ${RAIL_WIDTH}px;
  &::before,
  &::after {
    content: "";
    position: absolute;
    left: ${RAIL_WIDTH / 2 - 1}px;
    width: 2px;
    border-radius: 2px;
  }
  &::before {
    top: 0;
    height: ${DOT_TOP + DOT / 2}px;
    ${(p) => railSegment(p.$top, getNewLookTokens(p.theme).hairlineStrong)}
  }
  &::after {
    top: ${DOT_TOP + DOT / 2}px;
    bottom: 0;
    ${(p) => railSegment(p.$bottom, getNewLookTokens(p.theme).hairlineStrong)}
  }
  ${phone} {
    &::before {
      height: ${PHONE_DOT_TOP + DOT / 2}px;
    }
    &::after {
      top: ${PHONE_DOT_TOP + DOT / 2}px;
    }
  }
`;
const Dot = styled.span<{ $color: string; $pending: boolean }>`
  position: absolute;
  z-index: 1;
  top: ${DOT_TOP}px;
  left: 0;
  ${phone} {
    top: ${PHONE_DOT_TOP}px;
  }
  width: ${DOT}px;
  height: ${DOT}px;
  box-sizing: border-box;
  border-radius: 50%;
  ${(p) =>
    p.$pending
      ? css`
          background: ${getNewLookTokens(p.theme).panel};
          border: 2px solid ${p.$color};
        `
      : css`
          background: ${p.$color};
          box-shadow: 0 0 0 3px ${getNewLookTokens(p.theme).panel};
        `}
`;
const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 9px 0 10px;
  ${phone} {
    align-items: center;
    gap: 12px;
    min-height: ${PHONE_ROW}px;
    padding: 0;
  }
`;
const Main = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  /* Phone: "Take-Profit 23/39   21:10" on one line; a meta too long to fit
     beside the title drops to its own line instead of being cut. */
  ${phone} {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0 8px;
  }
`;
const Title = styled.span`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  ${phone} {
    flex: none;
    ${newLookPhoneType.body};
    font-weight: 500;
    white-space: nowrap;
  }
`;
const Total = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).muted};
  font-weight: 400;
`;
const Meta = styled.div`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  ${phone} {
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;
const Price = styled.div`
  flex: none;
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  ${phone} {
    ${newLookPhoneType.body};
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }
`;
